import { format } from 'shared/dateFns'
import Banner from '@precis-digital/kurama/Banner'
import { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown'
import { z } from 'zod'
import {
  createElement,
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useCallback,
  useMemo
} from 'react'
import { type UseMutateFunction } from '@tanstack/react-query'
import { type ConfigReq } from 'src/home/api'
import { type Metric, type ReportParameters } from 'src/home/reportTemplateParameters'
import {
  CONVERSION_EVENT_RELATED_METRICS,
  SCORECARD_METRICS_MAXIMUM_SELECTION,
  SCORECARD_METRICS_MINIMUM_SELECTION,
  type AllowedStandardReport,
  type HomePageFilterParameter,
  type HomePageTableDimension,
  type TimePeriodType
} from 'src/home/components/utils'
import {
  applyDetailedTableSettings,
  applyProgressOverTimeChartSettings,
  buildDetailedTableSettingsList,
  buildListAvailableConversionEventsResult,
  buildProgressOverTimeBreakdownOptionsFromReports,
  buildProgressOverTimeChartSettingsList,
  createActiveFiltersFromToolFilters,
  formatCurrentDetailedTableSettingsForToolDescription,
  formatCurrentProgressOverTimeChartSettingsForToolDescription,
  formatSelectedConversionEventForToolDescription,
  formatSelectedFiltersForToolDescription,
  getToolResultMessage,
  getToolResultSuccess,
  handleSelectConversionEventTool,
  handleSelectScorecardMetricsTool,
  HOME_VISUALIZATION_GUIDANCE,
  serializeFilterOptions,
  type SelectConversionEventToolParams,
  type SelectScorecardMetricsToolParams,
  type SetPageFiltersToolParams,
  type ToolResult,
  type UpdateDetailedTableSettingsToolParams,
  type UpdateProgressOverTimeChartSettingsToolParams
} from 'src/home/copilot/toolLogic'
import { type ReportingBaseConversionEventResp, type ReportType } from 'shared/api/analytics'
import { type ClientError } from 'shared/api/fetch'
import { type ActiveFilter, type Option } from 'shared/components/Filter'
import { type EventOption } from 'shared/components/ConversionEventsFlyout'
import { permissions } from 'shared/constants/permissions'
import { useCurrentClient } from 'shared/context/ClientContext'
import { zodToolSchema } from 'shared/copilot/hooks/useCopilotFrontendTool'
import {
  type CopilotPageElementRefsById,
  useCopilot,
  useRegisterCopilotActionDisplayResolvers,
  useRegisterCopilotPageContext,
  useRegisterCopilotPageElementRefs
} from 'shared/context/CopilotContext'
import { store as saveToStore } from 'shared/lstore'
import { useTranslation } from 'shared/translations'
import { type SummaryData } from 'src/home/components/Summary'
import { type ProgressOverTimeRecords } from 'src/home/components/ProgressOvertime'
import {
  type Report,
  type TransformToDetailedDataFn,
  type TransformToTimeSeriesByMetricAndBreakdownFn
} from 'src/home/transformations'
import {
  buildHomeCopilotDetailedTableInspectData,
  buildHomeCopilotPageContext,
  buildHomeCopilotProgressOverTimeChartInspectData,
  buildHomeCopilotScorecardMetrics
} from './context'
import { type HomeDetailedTableQueryState } from 'src/home/hooks/useHomeDetailedTableQueryState'
import { type HomeProgressOverTimeChartQueryState } from 'src/home/hooks/useHomeProgressOverTimeChartQueryState'
import { useFeedHomeCards } from 'src/recommendations/hooks'
import { type GroupedRecommendationItem } from 'src/recommendations/types'

interface UseHomeCopilotToolsArgs {
  selectedDate: {
    startDate: string
    endDate: string
  }
  setSelectedDate: Dispatch<
    SetStateAction<{
      startDate: string
      endDate: string
    }>
  >
  previousTimePeriod: [string, string]
  selectedStandardReportConfig: AllowedStandardReport | undefined
  reportParameters: ReportParameters | undefined
  selectedFilters: Array<ActiveFilter<HomePageFilterParameter>>
  setSelectedFilters: Dispatch<SetStateAction<Array<ActiveFilter<HomePageFilterParameter>>>>
  parameterOptions: Option[]
  canFilter: boolean
  isFilterOptionsLoaded: boolean
  selectedTopCardMetrics: Metric[]
  setSelectedTopCardMetrics: Dispatch<SetStateAction<Metric[]>>
  summaryData: SummaryData | null
  summaryDataForPreviousTimePeriod: SummaryData | null
  progressOverTimeRecords: Partial<Record<Metric, ProgressOverTimeRecords>>
  reports: Report[]
  reportsForPreviousTimePeriod: Report[]
  periodTypeToApply: TimePeriodType
  progressOverTimeChartQueryState: HomeProgressOverTimeChartQueryState
  detailedTableQueryState: HomeDetailedTableQueryState
  listOfMetricsToShowInDropDownWithBudgetMetrics: Metric[]
  reportRowCount: number
  isBudgetMode: boolean
  reportSupportsConversionEvents: boolean
  eventOptions: EventOption[] | undefined
  selectedConversionEvents: MultiSelectDropdownItem[] | undefined
  setSelectedConversionEvents: Dispatch<SetStateAction<MultiSelectDropdownItem[] | undefined>>
  getConversionNamesAndSources: UseMutateFunction<ReportingBaseConversionEventResp[], ClientError, ConfigReq>
  isLoadingConversionNamesAndSources: boolean
  isConversionNamesAndSourcesError: boolean
  hasSuccessfullyLoadedNecessaryData: boolean
  isDataError: boolean
  isNoReportViewConditionSatisfied: boolean
  isReportDataLoadingViewConditionSatisfied: boolean
  isReportDataKnownErrorViewConditionSatisfied: boolean
  isReportDataUnknownErrorViewConditionSatisfied: boolean
  isNoReportDataViewConditionSatisfied: boolean
  dateRangeCalendarRef: RefObject<HTMLButtonElement | null>
  filterBarRef: RefObject<HTMLDivElement | null>
  scorecardGroupRef: RefObject<HTMLDivElement | null>
  scorecardMetricElementRefsById: CopilotPageElementRefsById
  progressOverTimeChartRef: RefObject<HTMLDivElement | null>
  detailedTableRef: RefObject<HTMLDivElement | null>
  recommendationsRef: RefObject<HTMLDivElement | null>
  moreMenuButtonRef: RefObject<HTMLButtonElement | null>
}

/**
 * Derives a human-readable homepage data loading or error status for page context.
 * Maps toolbar view conditions to short status strings the agent can reason about.
 */
const getHomeDataStatus = ({
  hasSuccessfullyLoadedNecessaryData,
  isDataError,
  isNoReportViewConditionSatisfied,
  isReportDataLoadingViewConditionSatisfied,
  isReportDataKnownErrorViewConditionSatisfied,
  isReportDataUnknownErrorViewConditionSatisfied,
  isNoReportDataViewConditionSatisfied
}: Pick<
  UseHomeCopilotToolsArgs,
  | 'hasSuccessfullyLoadedNecessaryData'
  | 'isDataError'
  | 'isNoReportViewConditionSatisfied'
  | 'isReportDataLoadingViewConditionSatisfied'
  | 'isReportDataKnownErrorViewConditionSatisfied'
  | 'isReportDataUnknownErrorViewConditionSatisfied'
  | 'isNoReportDataViewConditionSatisfied'
>): string => {
  if (isNoReportViewConditionSatisfied) return 'no report selected'
  if (isReportDataLoadingViewConditionSatisfied) return 'report data is loading'
  if (isReportDataKnownErrorViewConditionSatisfied) return 'report has a known error'
  if (isReportDataUnknownErrorViewConditionSatisfied || isDataError) return 'report data has an unknown error'
  if (isNoReportDataViewConditionSatisfied) return 'no report data matches the current selection'
  if (hasSuccessfullyLoadedNecessaryData) return 'report data is loaded'
  return 'report data is not fully loaded yet'
}

/**
 * Counts items grouped by a string key extracted from each element.
 * Used to summarize recommendation severity and status distributions.
 */
const countBy = <T>(items: T[], getKey: (item: T) => string): Record<string, number> => {
  return items.reduce<Record<string, number>>((acc, item) => {
    const key = getKey(item)
    acc[key] = (acc[key] ?? 0) + 1
    return acc
  }, {})
}

/**
 * Derives a coarse recommendations panel status string for page context.
 * Reflects loading, error, empty, and connected-data-source states.
 */
const getRecommendationStatus = (
  isError: boolean,
  isLoading: boolean,
  isPending: boolean,
  noDataSources: boolean,
  visibleCardCount: number
): string => {
  if (isError) return 'error'
  if (isLoading || isPending) return 'loading'
  if (noDataSources) return 'no data sources connected'
  if (visibleCardCount === 0) return 'no active recommendations'
  return 'recommendations loaded'
}

/**
 * Builds recommendation summary counts and top items for copilot page context.
 * Includes severity and status breakdowns plus a preview of the first recommendations.
 */
const createRecommendationState = ({
  isLoading,
  isPending,
  isError,
  noDataSources,
  cards,
  allData
}: {
  isLoading: boolean
  isPending: boolean
  isError: boolean
  noDataSources: boolean
  cards: ReactNode[] | null
  allData: GroupedRecommendationItem[]
}): {
  status: string
  totalCount: number
  visibleCardCount: number
  severityCounts: Record<string, number>
  statusCounts: Record<string, number>
  topRecommendations: Array<{
    name: string
    severity: string
    status: string
    affectedEntities: number
    totalEntities: number
  }>
} => {
  const visibleCards = cards ?? []
  const status = getRecommendationStatus(isError, isLoading, isPending, noDataSources, visibleCards.length)

  return {
    status,
    totalCount: allData.length,
    visibleCardCount: visibleCards.length,
    severityCounts: countBy(allData, (recommendation) => recommendation.severity),
    statusCounts: countBy(allData, (recommendation) => recommendation.currentStatus),
    topRecommendations: allData.slice(0, 5).map((recommendation) => ({
      name: recommendation.name,
      severity: recommendation.severity,
      status: recommendation.currentStatus,
      affectedEntities: recommendation.totalNumEntitiesWithIssues,
      totalEntities: recommendation.totalNumEntities
    }))
  }
}

type BuildHomeCopilotToolsPageContextArgs = Pick<
  UseHomeCopilotToolsArgs,
  | 'selectedDate'
  | 'previousTimePeriod'
  | 'selectedFilters'
  | 'selectedTopCardMetrics'
  | 'summaryData'
  | 'summaryDataForPreviousTimePeriod'
  | 'progressOverTimeRecords'
  | 'reports'
  | 'reportsForPreviousTimePeriod'
  | 'periodTypeToApply'
  | 'reportRowCount'
  | 'isBudgetMode'
  | 'canFilter'
  | 'selectedConversionEvents'
  | 'hasSuccessfullyLoadedNecessaryData'
> & {
  currencyCode: string
  selectedStandardReportConfig: AllowedStandardReport | undefined
  reportParameters: ReportParameters | undefined
  detailedTableQueryState: HomeDetailedTableQueryState
  progressOverTimeChartCurrentState: {
    metrics: Metric[]
    marketIds: string[]
    channelIds: string[]
    aggregation: HomeProgressOverTimeChartQueryState['aggregation']
  }
  progressOverTimeChartQueryState: HomeProgressOverTimeChartQueryState
  recommendationState: ReturnType<typeof createRecommendationState>
  dataStatus: string
  translate: (key: Metric | `filter.${HomePageTableDimension}`) => string
}

/**
 * Assembles inspect payloads from hook state and delegates to `buildHomeCopilotPageContext`.
 * Translates metric and dimension ids to labels before building the final context object.
 */
const buildHomeCopilotToolsPageContext = ({
  currencyCode,
  selectedStandardReportConfig,
  reportParameters,
  selectedDate,
  previousTimePeriod,
  selectedFilters,
  selectedTopCardMetrics,
  summaryData,
  summaryDataForPreviousTimePeriod,
  progressOverTimeRecords,
  reports,
  reportsForPreviousTimePeriod,
  periodTypeToApply,
  detailedTableQueryState,
  progressOverTimeChartCurrentState,
  progressOverTimeChartQueryState,
  reportRowCount,
  recommendationState,
  selectedConversionEvents,
  isBudgetMode,
  canFilter,
  dataStatus,
  hasSuccessfullyLoadedNecessaryData,
  translate: t
}: BuildHomeCopilotToolsPageContextArgs): ReturnType<typeof buildHomeCopilotPageContext> => {
  const selectedTopCardMetricsWithLabels = selectedTopCardMetrics.map((metric) => ({
    id: metric,
    label: t(metric)
  }))

  return buildHomeCopilotPageContext({
    reportName: selectedStandardReportConfig?.descriptiveName,
    reportTemplate: selectedStandardReportConfig?.templateId,
    dateRange: selectedDate,
    previousTimePeriod,
    selectedFilters,
    selectedTopCardMetrics: selectedTopCardMetricsWithLabels,
    scorecardMetrics: buildHomeCopilotScorecardMetrics({
      selectedTopCardMetrics: selectedTopCardMetricsWithLabels,
      summaryData,
      summaryDataForPreviousTimePeriod,
      progressOverTimeRecords,
      currencyCode
    }),
    detailedTableState: {
      selectedDimensions: detailedTableQueryState.dimensions.map((dimension) => ({
        id: dimension,
        label: t(`filter.${dimension}`)
      })),
      selectedMetrics: detailedTableQueryState.metrics.map((metric) => ({
        id: metric,
        label: t(metric)
      }))
    },
    detailedTableInspectData: buildHomeCopilotDetailedTableInspectData({
      reports,
      reportsForPreviousTimePeriod,
      selectedDimensions: detailedTableQueryState.dimensions,
      selectedMetrics: detailedTableQueryState.metrics,
      transformToDetailedReportData: reportParameters?.transformations.transformToDetailedReportData as
        | TransformToDetailedDataFn<Report>
        | undefined,
      currencyCode,
      getDimensionLabel: (dimension) => t(`filter.${dimension}`),
      getMetricLabel: (metric) => t(metric)
    }),
    progressOverTimeChartInspectData: buildHomeCopilotProgressOverTimeChartInspectData({
      reports,
      reportsForPreviousTimePeriod,
      chartSettings: {
        ...progressOverTimeChartCurrentState,
        comparisonMode: progressOverTimeChartQueryState.isComparisonMode
      },
      dateRange: selectedDate,
      selectedPeriodType: periodTypeToApply,
      isBudgetMode,
      transformToTimeSeriesByMetricAndBreakdown: reportParameters?.transformations
        .transformToTimeSeriesByMetricAndBreakdown as
        | TransformToTimeSeriesByMetricAndBreakdownFn<Report, Metric>
        | undefined
    }),
    reportRowCount,
    timeSeriesChartState: {
      selectedMetrics: progressOverTimeChartCurrentState.metrics.join(', '),
      selectedBreakdowns:
        [...progressOverTimeChartCurrentState.marketIds, ...progressOverTimeChartCurrentState.channelIds].join(', ') ??
        'none',
      aggregation: progressOverTimeChartCurrentState.aggregation,
      comparisonMode: String(progressOverTimeChartQueryState.isComparisonMode)
    },
    recommendationState,
    selectedConversionEvents,
    isBudgetMode,
    canFilter,
    dataStatus,
    isDataLoaded: hasSuccessfullyLoadedNecessaryData && reportParameters != null
  })
}

/**
 * Registers all home-dashboard copilot frontend tools and page context.
 *
 * Wires tool handlers, inspect payloads, element refs, and action-display resolvers into
 * CopilotContext so the agent can read and mutate the visible dashboard state.
 */
export const useHomeCopilotTools = ({
  selectedDate,
  setSelectedDate,
  previousTimePeriod,
  selectedStandardReportConfig,
  reportParameters,
  selectedFilters,
  setSelectedFilters,
  parameterOptions,
  canFilter,
  isFilterOptionsLoaded,
  selectedTopCardMetrics,
  setSelectedTopCardMetrics,
  summaryData,
  summaryDataForPreviousTimePeriod,
  progressOverTimeRecords,
  reports,
  reportsForPreviousTimePeriod,
  periodTypeToApply,
  progressOverTimeChartQueryState,
  detailedTableQueryState,
  listOfMetricsToShowInDropDownWithBudgetMetrics,
  reportRowCount,
  isBudgetMode,
  reportSupportsConversionEvents,
  eventOptions,
  selectedConversionEvents,
  setSelectedConversionEvents,
  getConversionNamesAndSources,
  isLoadingConversionNamesAndSources,
  isConversionNamesAndSourcesError,
  hasSuccessfullyLoadedNecessaryData,
  isDataError,
  isNoReportViewConditionSatisfied,
  isReportDataLoadingViewConditionSatisfied,
  isReportDataKnownErrorViewConditionSatisfied,
  isReportDataUnknownErrorViewConditionSatisfied,
  isNoReportDataViewConditionSatisfied,
  dateRangeCalendarRef,
  filterBarRef,
  scorecardGroupRef,
  scorecardMetricElementRefsById,
  progressOverTimeChartRef,
  detailedTableRef,
  recommendationsRef,
  moreMenuButtonRef
}: UseHomeCopilotToolsArgs): void => {
  const { t } = useTranslation('home')
  const { currentClient } = useCurrentClient()
  const { useCopilotFrontendTool } = useCopilot()
  const getMetricLabel = useCallback((metricId: string) => t(metricId as Metric), [t])

  useRegisterCopilotActionDisplayResolvers({ getMetricLabel }, [getMetricLabel], 'home')

  const progressOverTimeChartCurrentState = useMemo(
    () => ({
      metrics: progressOverTimeChartQueryState.metrics,
      marketIds: progressOverTimeChartQueryState.markets.map((market) => market.value),
      channelIds: progressOverTimeChartQueryState.channels.map((channel) => channel.value),
      aggregation: progressOverTimeChartQueryState.aggregation
    }),
    [
      progressOverTimeChartQueryState.aggregation,
      progressOverTimeChartQueryState.channels,
      progressOverTimeChartQueryState.markets,
      progressOverTimeChartQueryState.metrics
    ]
  )

  const detailedTableCurrentState = useMemo(
    () => ({
      dimensions: detailedTableQueryState.dimensions,
      metrics: detailedTableQueryState.metrics
    }),
    [detailedTableQueryState.dimensions, detailedTableQueryState.metrics]
  )
  const {
    isPending: isLoadingRecommendations,
    isError: isRecommendationsError,
    noDataSources: hasNoRecommendationDataSources,
    cards: recommendationCards,
    allData: recommendationData
  } = useFeedHomeCards()

  const { marketOptions, channelOptions } = useMemo(
    () => buildProgressOverTimeBreakdownOptionsFromReports(reports),
    [reports]
  )

  const dataStatus = getHomeDataStatus({
    hasSuccessfullyLoadedNecessaryData,
    isDataError,
    isNoReportViewConditionSatisfied,
    isReportDataLoadingViewConditionSatisfied,
    isReportDataKnownErrorViewConditionSatisfied,
    isReportDataUnknownErrorViewConditionSatisfied,
    isNoReportDataViewConditionSatisfied
  })

  const pageContext = useMemo(() => {
    const context = buildHomeCopilotToolsPageContext({
      currencyCode: currentClient.currency,
      selectedStandardReportConfig,
      reportParameters,
      selectedDate,
      previousTimePeriod,
      selectedFilters,
      selectedTopCardMetrics,
      summaryData,
      summaryDataForPreviousTimePeriod,
      progressOverTimeRecords,
      reports,
      reportsForPreviousTimePeriod,
      periodTypeToApply,
      detailedTableQueryState,
      progressOverTimeChartCurrentState,
      progressOverTimeChartQueryState,
      reportRowCount,
      recommendationState: createRecommendationState({
        isLoading: isLoadingRecommendations,
        isPending: isLoadingRecommendations,
        isError: isRecommendationsError,
        noDataSources: hasNoRecommendationDataSources,
        cards: recommendationCards,
        allData: recommendationData
      }),
      selectedConversionEvents,
      isBudgetMode,
      canFilter,
      dataStatus,
      hasSuccessfullyLoadedNecessaryData,
      translate: (key) => t(key)
    })

    return {
      ...context,
      suggestedPrompts: [
        {
          label: 'How was performance last week?',
          message:
            "How was performance last week? I'm interested in both high level performance but also broken down by market."
        },
        {
          label: 'What could I do to improve performance?',
          message:
            "What could I do to improve performance? Please provide a brief overview of the current performance and which of Alvie's recommendations could help me improve it."
        }
      ]
    }
  }, [
    canFilter,
    currentClient.currency,
    dataStatus,
    hasSuccessfullyLoadedNecessaryData,
    isBudgetMode,
    isLoadingRecommendations,
    isRecommendationsError,
    detailedTableQueryState,
    previousTimePeriod,
    progressOverTimeChartCurrentState,
    progressOverTimeChartQueryState,
    progressOverTimeRecords,
    periodTypeToApply,
    reportParameters,
    reports,
    reportsForPreviousTimePeriod,
    hasNoRecommendationDataSources,
    recommendationCards,
    recommendationData,
    reportRowCount,
    selectedConversionEvents,
    selectedDate,
    selectedFilters,
    selectedStandardReportConfig,
    selectedTopCardMetrics,
    summaryData,
    summaryDataForPreviousTimePeriod,
    t
  ])

  useRegisterCopilotPageContext(pageContext, [pageContext])
  useRegisterCopilotPageElementRefs(
    'home',
    {
      date_range: [dateRangeCalendarRef],
      filters: [filterBarRef],
      scorecard_charts: [scorecardGroupRef],
      ...scorecardMetricElementRefsById,
      progress_over_time_chart: [progressOverTimeChartRef],
      detailed_table: [detailedTableRef],
      recommendations: [recommendationsRef]
    },
    [
      dateRangeCalendarRef,
      detailedTableRef,
      filterBarRef,
      progressOverTimeChartRef,
      recommendationsRef,
      scorecardGroupRef,
      scorecardMetricElementRefsById
    ]
  )

  useCopilotFrontendTool(
    {
      name: 'update_date_range',
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      description: `Update the date range for the page the user is currently looking at. Current date range: ${selectedDate.startDate} to ${selectedDate.endDate}. Do not call this tool if the current date range already matches the desired date range.`,
      parameters: zodToolSchema(
        z.object({
          startDate: z.string().describe('The start date to update the date range to in the format YYYY-MM-DD.'),
          endDate: z
            .string()
            .describe(
              `The end date to update the date range to in the format YYYY-MM-DD. Note that today is ${format(
                new Date(),
                'yyyy-MM-dd'
              )} and the end date should never be later than yesterday.`
            )
        })
      ),
      handler: (params: { startDate: string; endDate: string }) => {
        setSelectedDate({
          startDate: params.startDate,
          endDate: params.endDate
        })
      },
      elementRefs: [dateRangeCalendarRef],
      render: (props) => {
        return createElement(Banner, {
          variant: 'info',
          content: `Updated date range to ${String(props.args.startDate)} to ${String(props.args.endDate)}`
        })
      }
    },
    [selectedDate.endDate, selectedDate.startDate, setSelectedDate]
  )

  useCopilotFrontendTool(
    {
      name: 'list_filter_options',
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      description:
        'List the currently available homepage filter dimensions, operators, values, and the filters currently applied on the page. Use this before set_page_filters when you need to inspect valid filter inputs.',
      handler: () => {
        return serializeFilterOptions(parameterOptions, selectedFilters, canFilter, isFilterOptionsLoaded)
      }
    },
    [canFilter, isFilterOptionsLoaded, parameterOptions, selectedFilters]
  )

  useCopilotFrontendTool(
    {
      name: 'set_page_filters',
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      description: `Set or clear the homepage filters. Current page filters: ${formatSelectedFiltersForToolDescription(
        selectedFilters
      )}. Do not call this tool if the current filters already match the filters you want. Call list_filter_options first to inspect valid dimensions, operators, and values. Passing an empty filters array clears all page filters.`,
      parameters: zodToolSchema(
        z.object({
          filters: z
            .array(
              z.object({
                dimensionId: z
                  .string()
                  .describe('The filter dimension id from list_filter_options, for example market_name.'),
                operatorId: z
                  .string()
                  .describe('The operator id from list_filter_options, for example is, one_of, contains, or not_is.'),
                values: z
                  .array(z.string())
                  .describe(
                    'The filter values. Single-value operators should receive exactly one value. Multi-select operators can receive multiple values.'
                  )
              })
            )
            .describe(
              'The full list of filters that should be active after this tool call. Use an empty array to clear all filters.'
            )
        })
      ),
      handler: (params: SetPageFiltersToolParams): ToolResult => {
        const result = createActiveFiltersFromToolFilters(params.filters, parameterOptions)
        if (!('filters' in result)) return result

        setSelectedFilters(result.filters)
        const filterCount = result.filters.length
        const filterLabel = filterCount === 1 ? 'filter' : 'filters'
        const message =
          filterCount === 0 ? 'Cleared all page filters.' : `Updated page filters to ${filterCount} ${filterLabel}.`

        return {
          success: true,
          message
        }
      },
      elementRefs: [filterBarRef],
      render: (props) => {
        const message = getToolResultMessage(props.result, 'Updated page filters.')
        return createElement(Banner, {
          variant: getToolResultSuccess(props.result) === false ? 'warning' : 'info',
          content: message
        })
      }
    },
    [parameterOptions, selectedFilters, setSelectedFilters]
  )

  useCopilotFrontendTool(
    {
      name: 'list_available_metrics_for_scorecard_group',
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      description:
        'List the homepage scorecard group metrics that are available to show, including which metrics are currently selected.',
      handler: () => {
        return {
          reportTemplate: selectedStandardReportConfig?.templateId,
          isBudgetMode,
          minimumSelectedMetrics: SCORECARD_METRICS_MINIMUM_SELECTION,
          maximumSelectedMetrics: SCORECARD_METRICS_MAXIMUM_SELECTION,
          selectedMetrics: selectedTopCardMetrics.map((metric) => ({
            id: metric,
            label: t(metric)
          })),
          availableMetrics: listOfMetricsToShowInDropDownWithBudgetMetrics.map((metric) => ({
            id: metric,
            label: t(metric),
            selected: selectedTopCardMetrics.includes(metric)
          }))
        }
      }
    },
    [
      isBudgetMode,
      listOfMetricsToShowInDropDownWithBudgetMetrics,
      selectedStandardReportConfig,
      selectedTopCardMetrics,
      t
    ]
  )

  useCopilotFrontendTool(
    {
      name: 'select_metrics_in_scorecard_group',
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      description: `Select which metrics appear in the homepage scorecard group. Current selected metrics: ${
        selectedTopCardMetrics.length > 0
          ? selectedTopCardMetrics.map((metric) => `${metric} (${t(metric)})`).join(', ')
          : 'none'
      }. Do not call this tool if the current selected metrics already match the desired scorecard metrics. Call list_available_metrics_for_scorecard_group first if you need the available metric ids.`,
      parameters: zodToolSchema(
        z.object({
          metrics: z
            .array(z.string())
            .describe(
              'The ordered metric ids to show in the scorecard group. Provide the full desired selection, not only the changes.'
            )
        })
      ),
      handler: (params: SelectScorecardMetricsToolParams): ToolResult =>
        handleSelectScorecardMetricsTool({
          params,
          reportParameters,
          listOfMetricsToShowInDropDownWithBudgetMetrics,
          setSelectedTopCardMetrics,
          saveMetricsToStore: (metrics) => {
            if (reportParameters == null) return
            saveToStore(reportParameters.localStorageKeyForMetrics, JSON.stringify(metrics))
          }
        }),
      elementRefs: [scorecardGroupRef],
      render: (props) => {
        const message = getToolResultMessage(props.result, 'Updated scorecard metrics.')
        return createElement(Banner, {
          variant: getToolResultSuccess(props.result) === false ? 'warning' : 'info',
          content: message
        })
      }
    },
    [
      listOfMetricsToShowInDropDownWithBudgetMetrics,
      reportParameters,
      selectedTopCardMetrics,
      setSelectedTopCardMetrics,
      t
    ]
  )

  const loadConversionEventOptions = (): void => {
    if (reportParameters == null || !reportSupportsConversionEvents || selectedStandardReportConfig == null) return

    getConversionNamesAndSources({
      clientId: currentClient.id,
      targetCurrencyCode: currentClient.currency,
      id: selectedStandardReportConfig.configId.toString(),
      report: selectedStandardReportConfig.templateId as ReportType,
      filters: [
        {
          field: 'date',
          operator: 'BETWEEN',
          value: [selectedDate.startDate, selectedDate.endDate]
        }
      ],
      ...reportParameters.conversionEventVariables
    })
  }

  useCopilotFrontendTool(
    {
      name: 'list_available_conversion_events',
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      available: reportSupportsConversionEvents,
      description:
        'List the homepage conversion events that can be selected as the current goal, along with the currently selected conversion event. Use this before select_conversion_event when the user explicitly asks about a goal like purchases or add to carts.',
      handler: () => {
        if (eventOptions == null && !isLoadingConversionNamesAndSources && !isConversionNamesAndSourcesError) {
          loadConversionEventOptions()
        }

        return buildListAvailableConversionEventsResult({
          isLoadingConversionNamesAndSources,
          eventOptions,
          selectedConversionEvents,
          selectedTopCardMetrics,
          getMetricLabel: (metric) => t(metric)
        })
      }
    },
    [
      currentClient.currency,
      currentClient.id,
      eventOptions,
      getConversionNamesAndSources,
      isConversionNamesAndSourcesError,
      isLoadingConversionNamesAndSources,
      reportParameters,
      selectedConversionEvents,
      selectedDate.endDate,
      selectedDate.startDate,
      selectedStandardReportConfig,
      selectedTopCardMetrics,
      t
    ]
  )

  useCopilotFrontendTool(
    {
      name: 'select_conversion_event',
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      available: reportSupportsConversionEvents,
      description: `Select the homepage conversion event goal. Use this only when the user explicitly talks about a goal such as purchases, add to carts, signups, or another conversion event. The conversion event affects metrics such as ${CONVERSION_EVENT_RELATED_METRICS.map(
        (metric) => `${metric} (${t(metric)})`
      ).join(', ')}. Current selected conversion event: ${formatSelectedConversionEventForToolDescription(
        selectedConversionEvents
      )}. Do not call this tool if the current conversion event already matches the desired goal.`,
      parameters: zodToolSchema(
        z.object({
          eventName: z.string().describe('The conversion event name from list_available_conversion_events.'),
          eventSource: z
            .string()
            .describe(
              'Optional conversion event source from list_available_conversion_events. Provide this when multiple sources have the same event name.'
            )
            .optional()
        })
      ),
      handler: (params: SelectConversionEventToolParams): ToolResult =>
        handleSelectConversionEventTool({
          params,
          selectedStandardReportConfig,
          reportParameters,
          eventOptions,
          selectedConversionEvents,
          setSelectedConversionEvents,
          loadConversionEventOptions
        }),
      elementRefs: [moreMenuButtonRef],
      render: (props) => {
        const message = getToolResultMessage(props.result, 'Updated conversion event.')
        return createElement(Banner, {
          variant: getToolResultSuccess(props.result) === false ? 'warning' : 'info',
          content: message
        })
      }
    },
    [
      currentClient.currency,
      currentClient.id,
      eventOptions,
      getConversionNamesAndSources,
      isConversionNamesAndSourcesError,
      isLoadingConversionNamesAndSources,
      reportParameters,
      selectedConversionEvents,
      selectedDate.endDate,
      selectedDate.startDate,
      selectedStandardReportConfig,
      setSelectedConversionEvents,
      t
    ]
  )

  useCopilotFrontendTool(
    {
      name: 'list_available_progress_over_time_chart_settings',
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      description: `List the progress over time chart metrics, market and channel breakdown options, aggregation settings, constraints, and current selection. ${HOME_VISUALIZATION_GUIDANCE.progressOverTimeChart} Use this before update_progress_over_time_chart_settings when you need valid ids or constraint details.`,
      handler: () => {
        return buildProgressOverTimeChartSettingsList({
          currentState: progressOverTimeChartCurrentState,
          availableMetrics: listOfMetricsToShowInDropDownWithBudgetMetrics,
          marketOptions,
          channelOptions,
          getMetricLabel: (metric) => t(metric),
          isDataLoaded: hasSuccessfullyLoadedNecessaryData && reportParameters != null
        })
      }
    },
    [
      channelOptions,
      hasSuccessfullyLoadedNecessaryData,
      listOfMetricsToShowInDropDownWithBudgetMetrics,
      marketOptions,
      progressOverTimeChartCurrentState,
      reportParameters,
      t
    ]
  )

  useCopilotFrontendTool(
    {
      name: 'update_progress_over_time_chart_settings',
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      description: `Update the progress over time chart settings. ${
        HOME_VISUALIZATION_GUIDANCE.progressOverTimeChart
      } Current settings: ${formatCurrentProgressOverTimeChartSettingsForToolDescription({
        ...progressOverTimeChartCurrentState,
        marketOptions,
        channelOptions,
        getMetricLabel: (metric) => t(metric)
      })} Do not call this tool if the current settings already match the desired configuration. Call list_available_progress_over_time_chart_settings first when you need available metric ids, breakdown ids, or constraint details.`,
      parameters: zodToolSchema(
        z.object({
          metrics: z
            .array(z.string())
            .describe(
              'The metric ids to show in the progress over time chart. Provide the full desired selection from list_available_progress_over_time_chart_settings.'
            ),
          breakdown_markets: z
            .array(z.string())
            .describe(
              'Optional market ids for the breakdown from list_available_progress_over_time_chart_settings. Provide the full desired market selection, or an empty array to clear markets. Do not combine with channels.'
            )
            .optional(),
          breakdown_channels: z
            .array(z.string())
            .describe(
              'Optional channel ids for the breakdown from list_available_progress_over_time_chart_settings. Provide the full desired channel selection, or an empty array to clear channels. Do not combine with markets.'
            )
            .optional(),
          aggregation: z
            .enum(['daily', 'cumulative'])
            .describe(
              'Optional aggregation setting from list_available_progress_over_time_chart_settings. Defaults to daily.'
            )
            .optional()
        })
      ),
      handler: (params: UpdateProgressOverTimeChartSettingsToolParams): ToolResult => {
        if (reportParameters == null || !hasSuccessfullyLoadedNecessaryData) {
          return {
            success: false,
            message: 'Report data is not loaded yet, so the progress over time chart cannot be updated.'
          }
        }

        if (listOfMetricsToShowInDropDownWithBudgetMetrics.length === 0) {
          return {
            success: false,
            message: 'No metrics are available for the progress over time chart on the current report.'
          }
        }

        return applyProgressOverTimeChartSettings({
          params,
          currentState: progressOverTimeChartCurrentState,
          availableMetrics: listOfMetricsToShowInDropDownWithBudgetMetrics,
          marketOptions,
          channelOptions,
          marketsSubCategoryLabel: t('markets'),
          channelsSubCategoryLabel: t('channels'),
          setChartState: {
            setMetrics: progressOverTimeChartQueryState.setMetrics,
            setChannels: progressOverTimeChartQueryState.setChannels,
            setMarkets: progressOverTimeChartQueryState.setMarkets,
            setAggregation: progressOverTimeChartQueryState.setAggregation
          }
        })
      },
      elementRefs: [progressOverTimeChartRef],
      render: (props) => {
        const message = getToolResultMessage(props.result, 'Updated progress over time chart settings.')
        return createElement(Banner, {
          variant: getToolResultSuccess(props.result) === false ? 'warning' : 'info',
          content: message
        })
      }
    },
    [
      channelOptions,
      hasSuccessfullyLoadedNecessaryData,
      listOfMetricsToShowInDropDownWithBudgetMetrics,
      marketOptions,
      progressOverTimeChartCurrentState,
      progressOverTimeChartQueryState,
      reportParameters,
      t
    ]
  )

  const getDimensionLabel = useCallback((dimensionId: HomePageTableDimension) => t(`filter.${dimensionId}`), [t])

  useCopilotFrontendTool(
    {
      name: 'list_available_detailed_table_settings',
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      description: `List the detailed table dimensions, metrics, constraints, and current column selection. ${HOME_VISUALIZATION_GUIDANCE.detailedTable} Use this before update_detailed_table_settings when you need valid dimension or metric ids.`,
      handler: () => {
        return buildDetailedTableSettingsList({
          currentState: detailedTableCurrentState,
          availableMetrics: listOfMetricsToShowInDropDownWithBudgetMetrics,
          getDimensionLabel,
          getMetricLabel: (metric) => t(metric),
          isDataLoaded: hasSuccessfullyLoadedNecessaryData && reportParameters != null
        })
      }
    },
    [
      detailedTableCurrentState,
      getDimensionLabel,
      hasSuccessfullyLoadedNecessaryData,
      listOfMetricsToShowInDropDownWithBudgetMetrics,
      reportParameters,
      t
    ]
  )

  useCopilotFrontendTool(
    {
      name: 'update_detailed_table_settings',
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      description: `Update the detailed data table columns. ${
        HOME_VISUALIZATION_GUIDANCE.detailedTable
      } Current settings: ${formatCurrentDetailedTableSettingsForToolDescription({
        dimensions: detailedTableCurrentState.dimensions,
        metrics: detailedTableCurrentState.metrics,
        getDimensionLabel,
        getMetricLabel: (metric) => t(metric)
      })} Do not call this tool if the current settings already match the desired configuration. Call list_available_detailed_table_settings first when you need available dimension or metric ids.`,
      parameters: zodToolSchema(
        z.object({
          dimensions: z
            .array(z.string())
            .describe(
              'The dimension ids that define how table rows are grouped. Provide the full desired selection from list_available_detailed_table_settings.'
            ),
          metrics: z
            .array(z.string())
            .describe(
              'The metric ids to show as table columns. Provide the full desired selection from list_available_detailed_table_settings.'
            )
        })
      ),
      handler: (params: UpdateDetailedTableSettingsToolParams): ToolResult => {
        if (reportParameters == null || !hasSuccessfullyLoadedNecessaryData) {
          return {
            success: false,
            message: 'Report data is not loaded yet, so the detailed table cannot be updated.'
          }
        }

        if (listOfMetricsToShowInDropDownWithBudgetMetrics.length === 0) {
          return {
            success: false,
            message: 'No metrics are available for the detailed table on the current report.'
          }
        }

        return applyDetailedTableSettings({
          params,
          currentState: detailedTableCurrentState,
          availableMetrics: listOfMetricsToShowInDropDownWithBudgetMetrics,
          setSelectedOptions: detailedTableQueryState.setSelectedOptions
        })
      },
      elementRefs: [detailedTableRef],
      render: (props) => {
        const message = getToolResultMessage(props.result, 'Updated detailed table settings.')
        return createElement(Banner, {
          variant: getToolResultSuccess(props.result) === false ? 'warning' : 'info',
          content: message
        })
      }
    },
    [
      detailedTableCurrentState,
      detailedTableQueryState,
      detailedTableRef,
      getDimensionLabel,
      hasSuccessfullyLoadedNecessaryData,
      listOfMetricsToShowInDropDownWithBudgetMetrics,
      reportParameters,
      t
    ]
  )
}
